// Enhanced API service for the Compliance Assessment application
import axios from 'axios';
import i18n from '../i18n';
import { 
  User, 
  Policy, 
  SystemStandard,
  PublicFramework,
  PolicyReview, 
  LoginCredentials, 
  RegisterData, 
  AuthResponse, 
  FileUploadResponse,
  DashboardStats
} from '../types';

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // send HttpOnly auth cookies with every request
});

let globalLogoutCallback: (() => void) | null = null;

// Set global logout callback for API interceptor
export const setGlobalLogoutCallback = (callback: () => void) => {
  globalLogoutCallback = callback;
};

// Perform logout with proper cleanup
const performLogout = () => {
  if (globalLogoutCallback) {
    globalLogoutCallback();
  } else {
    window.location.href = '/login';
  }
};

// Token refresh state to prevent concurrent refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(null);
    }
  });
  failedQueue = [];
};

// Response interceptor to handle auth errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Never intercept auth endpoints themselves to avoid infinite loops
    const isAuthEndpoint = originalRequest?.url?.includes('/api/auth/refresh') ||
                           originalRequest?.url?.includes('/api/auth/login');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // Check if this is a session timeout (specific message from backend)
      const errorMessage = error.response?.data?.detail || '';
      if (errorMessage.includes('Session expired')) {
        console.log('Session expired - logging out user');
        performLogout();
        return Promise.reject(error);
      }

      // If already refreshing, queue this request to retry after refresh completes
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log('Attempting silent token refresh via cookie...');
        await api.post('/api/auth/refresh'); // refresh_token cookie sent automatically
        console.log('Token refresh successful');
        isRefreshing = false;
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        console.log('Token refresh failed - logging out user');
        isRefreshing = false;
        processQueue(refreshError);
        performLogout();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Enhanced Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const formData = new URLSearchParams();
    formData.append('username', credentials.email);
    formData.append('password', credentials.password);

    const response = await api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
    // Tokens are set as HttpOnly cookies by the server
    return response.data;
  },

  loginWithGoogle: async (token: string): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/login/google', { token });
    // Tokens are set as HttpOnly cookies by the server
    return response.data;
  },

  register: async (userData: RegisterData): Promise<User> => {
    const response = await api.post('/api/auth/register', userData);
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  updatePreferredLanguage: async (language: string): Promise<User> => {
    const response = await api.patch('/api/auth/me/language', { language });
    return response.data;
  },

  refreshToken: async (): Promise<void> => {
    await api.post('/api/auth/refresh'); // refresh_token cookie sent automatically
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      authAPI.clearAuthToken();
    }
  },

  // Token management - no-ops; tokens are now in HttpOnly cookies set by the server
  setAuthToken: (_token: string) => {},
  clearAuthToken: () => {},
  getStoredToken: (): string | null => null,
  getStoredRefreshToken: (): string | null => null,
};

// MFA API
export const mfaAPI = {
  getStatus: async () => {
    const response = await api.get('/api/auth/mfa/status');
    return response.data;
  },
  setup: async () => {
    const response = await api.post('/api/auth/mfa/setup');
    return response.data;
  },
  verifySetup: async (code: string) => {
    const response = await api.post('/api/auth/mfa/verify-setup', { code });
    return response.data;
  },
  disable: async (code: string) => {
    const response = await api.post('/api/auth/mfa/disable', { code });
    return response.data;
  },
  verifyLogin: async (mfaToken: string, code: string, isBackupCode: boolean = false) => {
    const response = await api.post('/api/auth/mfa/verify-login', {
      mfa_token: mfaToken,
      code,
      is_backup_code: isBackupCode,
    });
    return response.data;
  },
  regenerateBackupCodes: async (code: string) => {
    const response = await api.post('/api/auth/mfa/regenerate-backup-codes', { code });
    return response.data;
  },
};

// Policies API
export const policiesAPI = {
  getAll: async (): Promise<Policy[]> => {
    const response = await api.get('/api/policies');
    return response.data;
  },

  getById: async (id: string): Promise<Policy> => {
    const response = await api.get(`/api/policies/${id}`);
    return response.data;
  },

  upload: async (files: File[], name?: string): Promise<FileUploadResponse> => {
    const formData = new FormData();
    
    // Append all files
    files.forEach(file => {
      formData.append('files', file);
    });
    
    if (name) formData.append('name', name);

    const response = await api.post('/api/policies', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/policies/${id}`);
  }
};

// (Legacy flat-file standardsAPI removed — use systemStandardsAPI instead.)

// Reviews API
export const reviewsAPI = {
  getAll: async (): Promise<PolicyReview[]> => {
    const response = await api.get('/api/reviews');
    return response.data;
  },

  getById: async (id: string): Promise<PolicyReview> => {
    const response = await api.get(`/api/reviews/${id}`);
    return response.data;
  },

  create: async (policyId: string, standardId?: string, systemStandardId?: string): Promise<PolicyReview> => {
    const response = await api.post('/api/reviews', {
      policy_id: parseInt(policyId),
      standard_id: standardId ? parseInt(standardId) : undefined,
      system_standard_id: systemStandardId ? parseInt(systemStandardId) : undefined
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/reviews/${id}`);
  },

  downloadReport: async (id: string): Promise<Blob> => {
    const response = await api.get(`/api/reviews/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getStatus: async (id: string): Promise<{
    id: number;
    status: string;
    compliance_score: number | null;
    created_at: string;
    completed_at: string | null;
    total_sections: number;
    completed_sections: number;
    progress_percentage: number;
    report_generated: boolean;
  }> => {
    const response = await api.get(`/api/reviews/${id}/status`);
    return response.data;
  },

  getDetails: async (id: string): Promise<PolicyReview> => {
    const response = await api.get(`/api/reviews/${id}`);
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/api/dashboard/stats');
    return response.data;
  }
};

// System Standards API (for users)
export const systemStandardsAPI = {
  getAll: async (): Promise<SystemStandard[]> => {
    const response = await api.get('/api/system-standards');
    return response.data;
  },

  getById: async (id: string): Promise<SystemStandard> => {
    const response = await api.get(`/api/system-standards/${id}`);
    return response.data;
  },

  getSections: async (standardId: string): Promise<any> => {
    const lang = (i18n.language || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const response = await api.get(`/api/system-standards/${standardId}/sections`, { params: { lang } });
    return response.data;
  }
};

// Evidence API
export const evidenceAPI = {
  getRequiredEvidence: async (params: {
    system_standard_id: number;
    selected_sections?: string[];
    selected_subsections?: { [key: string]: string[] };
  }): Promise<any> => {
    const language = (i18n.language || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const response = await api.post('/api/evidence/requirements', { ...params, language });
    return response.data;
  }
};

// Public API (no auth required)
export const publicAPI = {
  getFrameworks: async (): Promise<PublicFramework[]> => {
    const response = await api.get('/api/public/frameworks');
    return response.data;
  }
};

// Health check
export const healthAPI = {
  check: async (): Promise<{ status: string }> => {
    const response = await api.get('/health');
    return response.data;
  }
};

// Admin Standards API (System Standards)
export const adminStandardsAPI = {
  getAll: async (): Promise<SystemStandard[]> => {
    const response = await api.get('/api/admin/system-standards');
    return response.data;
  },
  getById: async (id: string): Promise<SystemStandard> => {
    const response = await api.get(`/api/admin/system-standards/${id}`);
    return response.data;
  },
  add: async (standardData: any): Promise<SystemStandard> => {
    const response = await api.post('/api/admin/system-standards', standardData);
    return response.data;
  },
  update: async (id: string, updates: Partial<SystemStandard>): Promise<SystemStandard> => {
    const response = await api.put(`/api/admin/system-standards/${id}`, updates);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/admin/system-standards/${id}`);
  },
};

// Templates API
export const templatesAPI = {
  getAll: async (standardType?: string): Promise<any[]> => {
    // If standardType is provided, filter by it
    const params = standardType ? `?standard_type=${encodeURIComponent(standardType)}` : '';
    const response = await api.get(`/api/templates${params}`);
    return response.data;
  },
  getById: async (id: string): Promise<any> => {
    const response = await api.get(`/api/templates/${id}`);
    return response.data;
  }
};

// --- Admin API ---
export const adminAPI = {
  // User Management
  getUsers: async (params?: { skip?: number; limit?: number; search?: string; subscription_tier?: string; status?: string }): Promise<any> => {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.append('skip', params.skip.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.subscription_tier) searchParams.append('subscription_tier', params.subscription_tier);
    if (params?.status) searchParams.append('status', params.status);
    
    const response = await api.get(`/api/admin/users?${searchParams.toString()}`);
    return response.data.users || response.data;
  },
  
  updateUser: async (userId: number, updates: any): Promise<any> => {
    const response = await api.put(`/api/admin/users/${userId}`, updates);
    return response.data;
  },
  
  deleteUser: async (userId: number): Promise<any> => {
    const response = await api.delete(`/api/admin/users/${userId}`);
    return response.data;
  },

  changeUserPassword: async (userId: number, newPassword: string): Promise<any> => {
    const response = await api.post(`/api/admin/users/${userId}/change-password`, {
      new_password: newPassword
    });
    return response.data;
  },

  // Analytics
  getAnalytics: async (period: string = '30d'): Promise<any> => {
    const response = await api.get(`/api/admin/analytics?period=${period}`);
    return response.data;
  },

  // System Status
  getSystemStatus: async (): Promise<any> => {
    const response = await api.get('/api/admin/system_status');
    return response.data;
  },

  // Configuration
  getAppSettings: async (): Promise<any> => {
    const response = await api.get('/api/admin/config/app-settings');
    return response.data;
  },

  getIntegrations: async (): Promise<any> => {
    const response = await api.get('/api/admin/config/integrations');
    return response.data;
  },

  getEnvironmentInfo: async (): Promise<any> => {
    const response = await api.get('/api/admin/config/environment');
    return response.data;
  },

  getSystemSettings: async (): Promise<any> => {
    const response = await api.get('/api/admin/config/system-settings');
    return response.data;
  },

  updateSystemSettings: async (settings: { maintenance_mode?: boolean; allow_signups?: boolean; require_email_verification?: boolean }): Promise<any> => {
    const response = await api.post('/api/admin/config/system-settings', settings);
    return response.data;
  },

  // List available models from the configured Ollama instance
  getOllamaModels: async (): Promise<{ models: string[]; base_url: string; error?: string }> => {
    const response = await api.get('/api/admin/config/ollama-models');
    return response.data;
  },

  // AI Runtime Configuration
  getAISettings: async (): Promise<any> => {
    const response = await api.get('/api/admin/config/ai-settings');
    return response.data;
  },

  updateAISettings: async (settings: {
    ollama_base_url?: string;
    ollama_model?: string;
    api_key?: string;
    num_ctx?: number;
    num_predict?: number;
    temperature?: number;
    top_p?: number;
    top_k?: number;
    repeat_penalty?: number;
    repeat_last_n?: number;
    request_timeout_seconds?: number;
  }): Promise<any> => {
    const response = await api.post('/api/admin/config/ai-settings', settings);
    return response.data;
  },

  triggerReindex: async (): Promise<{ success: boolean; message: string; results: any }> => {
    const response = await api.post('/api/admin/config/reindex-chromadb');
    return response.data;
  },

  // Embedding/Semantic Retrieval Configuration
  getEmbeddingModels: async (): Promise<{ active_model: string; cached_models: string[]; available_models: string[] }> => {
    const response = await api.get('/api/admin/config/embedding-models');
    return response.data;
  },

  getEmbeddingSettings: async (): Promise<any> => {
    const response = await api.get('/api/admin/config/embedding-settings');
    return response.data;
  },

  updateEmbeddingSettings: async (settings: {
    embedding_model?: string;
    semantic_candidates_top_k?: number;
    semantic_max_useful_chunks?: number;
    semantic_min_similarity?: number;
    semantic_max_context_chars?: number;
    semantic_prefilter_min_similarity?: number;
    evidence_max_files_per_measure?: number;
    evidence_max_chars_per_file?: number;
    evidence_max_file_size_mb?: number;
  }): Promise<any> => {
    const response = await api.post('/api/admin/config/embedding-settings', settings);
    return response.data;
  },

  // LLM usage analytics
  getAIUsageAnalytics: async (): Promise<any> => {
    const response = await api.get('/api/admin/analytics/ai-usage');
    return response.data;
  },

  // Email Configuration
  getEmailSettings: async (): Promise<any> => {
    const response = await api.get('/api/admin/config/email-settings');
    return response.data;
  },

  updateEmailSettings: async (settings: { smtp_host?: string; smtp_port?: string; smtp_user?: string; smtp_password?: string; mail_from?: string; mail_from_name?: string }): Promise<any> => {
    const response = await api.post('/api/admin/config/email-settings', settings);
    return response.data;
  },

  testEmailConnection: async (recipient?: string): Promise<any> => {
    const response = await api.post('/api/admin/config/test-email', { recipient });
    return response.data;
  },

  // Chatwoot Configuration
  getChatwootSettings: async (): Promise<any> => {
    const response = await api.get('/api/admin/config/chatwoot-settings');
    return response.data;
  },

  updateChatwootSettings: async (settings: { chatwoot_base_url?: string; chatwoot_website_token?: string }): Promise<any> => {
    const response = await api.post('/api/admin/config/chatwoot-settings', settings);
    return response.data;
  },

  // Admin Notifications
  getNotifications: async (params?: { limit?: number; offset?: number; category?: string }): Promise<any> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    if (params?.category && params.category !== 'all') searchParams.append('category', params.category);
    const response = await api.get(`/api/admin/notifications?${searchParams.toString()}`);
    return response.data;
  },

  // Backup & Recovery
  createBackup: async (components: string[], description?: string): Promise<any> => {
    const response = await api.post('/api/admin/backup/create', { components, description });
    return response.data;
  },

  listBackups: async (): Promise<any> => {
    const response = await api.get('/api/admin/backup/list');
    return response.data;
  },

  getBackupStatistics: async (): Promise<any> => {
    const response = await api.get('/api/admin/backup/statistics');
    return response.data;
  },

  getBackupInfo: async (backupName: string): Promise<any> => {
    const response = await api.get(`/api/admin/backup/${backupName}`);
    return response.data;
  },

  restoreBackup: async (backupName: string, components?: string[], force?: boolean): Promise<any> => {
    const response = await api.post('/api/admin/backup/restore', { backup_name: backupName, components, force });
    return response.data;
  },

  downloadBackup: async (backupName: string): Promise<Blob> => {
    const response = await api.get(`/api/admin/backup/download/${backupName}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  deleteBackup: async (backupName: string): Promise<any> => {
    const response = await api.delete(`/api/admin/backup/${backupName}`);
    return response.data;
  },

  // Dashboard Stats
  getDashboardStats: async (): Promise<any> => {
    const response = await api.get('/api/admin/dashboard');
    return response.data;
  },

  // Content Management
  getPolicies: async (params?: { skip?: number; limit?: number; search?: string }): Promise<any> => {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.append('skip', params.skip.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    
    const response = await api.get(`/api/admin/policies?${searchParams.toString()}`);
    return response.data;
  },

  getReviews: async (params?: { skip?: number; limit?: number }): Promise<any> => {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.append('skip', params.skip.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    
    const response = await api.get(`/api/admin/reviews?${searchParams.toString()}`);
    return response.data;
  },

  // Subscription Management
  getSubscriptions: async (): Promise<any> => {
    const response = await api.get('/api/admin/subscriptions');
    return response.data;
  },

  updateUserSubscription: async (userId: number, newTier: string, reason?: string): Promise<any> => {
    const response = await api.put(`/api/admin/users/${userId}/subscription`, {
      new_tier: newTier,
      reason
    });
    return response.data;
  },

  // Subscription Plans Management
  getSubscriptionPlans: async (): Promise<any> => {
    const response = await api.get('/api/admin/subscription-plans');
    return response.data;
  },

  getSubscriptionTiers: async (): Promise<any> => {
    const response = await api.get('/api/admin/subscription-tiers');
    return response.data;
  },

  createSubscriptionPlan: async (planData: any): Promise<any> => {
    const response = await api.post('/api/admin/subscription-plans', planData);
    return response.data;
  },

  updateSubscriptionPlan: async (planId: number, planData: any): Promise<any> => {
    const response = await api.put(`/api/admin/subscription-plans/${planId}`, planData);
    return response.data;
  },

  deleteSubscriptionPlan: async (planId: number): Promise<any> => {
    const response = await api.delete(`/api/admin/subscription-plans/${planId}`);
    return response.data;
  },

  getSubscriptionStats: async (): Promise<any> => {
    const response = await api.get('/api/admin/analytics/subscription-stats');
    return response.data;
  },

  getUserUsage: async (): Promise<any> => {
    const response = await api.get('/api/admin/user-usage');
    return response.data;
  },

  // Billing Management
  getBillingOverview: async (): Promise<any> => {
    const response = await api.get('/api/admin/billing');
    return response.data;
  },

  getUserAnalytics: async (): Promise<any> => {
    const response = await api.get('/api/admin/analytics/users');
    return response.data;
  },

  getRevenueAnalytics: async (period: string = '30d'): Promise<any> => {
    const response = await api.get(`/api/admin/analytics/revenue?period=${period}`);
    return response.data;
  },

  // Usage Analytics
  getUsageOverview: async (): Promise<any> => {
    const response = await api.get('/api/admin/usage/overview');
    return response.data;
  },

  getUsageAnalytics: async (period: string = '30d'): Promise<any> => {
    const response = await api.get(`/api/admin/analytics/usage?period=${period}`);
    return response.data;
  },

  // Review Report Download
  downloadReviewReport: async (reviewId: number): Promise<any> => {
    const response = await api.get(`/api/admin/reviews/${reviewId}/report`, {
      responseType: 'blob'
    });
    return response;
  },

  // Delete Review
  deleteReview: async (reviewId: number): Promise<any> => {
    const response = await api.delete(`/api/reviews/${reviewId}`);
    return response.data;
  },

  // Top Users Analytics
  getTopUsers: async (period: string = '30d'): Promise<any> => {
    const response = await api.get(`/api/admin/analytics/top-users?period=${period}`);
    return response.data;
  },

  // Performance Metrics
  getPerformanceMetrics: async (): Promise<any> => {
    const response = await api.get('/api/admin/analytics/performance');
    return response.data;
  },

  // Security & Compliance
  getAuditLogs: async (params?: any): Promise<any> => {
    const searchParams = new URLSearchParams();
    if (params?.user_id) searchParams.append('user_id', params.user_id.toString());
    if (params?.action) searchParams.append('action', params.action);
    if (params?.resource_type) searchParams.append('resource_type', params.resource_type);
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    if (params?.success !== undefined && params?.success !== null) searchParams.append('success', params.success.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    
    const response = await api.get(`/api/admin/audit-logs?${searchParams.toString()}`);
    return response.data;
  },

  getAuditStatistics: async (params?: any): Promise<any> => {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    
    const response = await api.get(`/api/admin/audit-logs/statistics?${searchParams.toString()}`);
    return response.data;
  },

  getComplianceDashboard: async (): Promise<any> => {
    const response = await api.get('/api/admin/compliance/dashboard');
    return response.data;
  },

  getSecurityIncidents: async (params?: any): Promise<any> => {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    if (params?.severity) searchParams.append('severity', params.severity);
    
    const response = await api.get(`/api/admin/security/incidents?${searchParams.toString()}`);
    return response.data;
  },

  getAccessControlStatus: async (): Promise<any> => {
    const response = await api.get('/api/admin/security/access-controls');
    return response.data;
  },

  getSecurityStatus: async (): Promise<any> => {
    const response = await api.get('/api/admin/security/status');
    return response.data;
  },

  getSOC2Report: async (params?: any): Promise<any> => {
    const searchParams = new URLSearchParams();
    if (params?.report_type) searchParams.append('report_type', params.report_type);
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    
    const response = await api.get(`/api/admin/soc2/report?${searchParams.toString()}`);
    return response.data;
  },

  exportAuditLogs: async (params?: any): Promise<any> => {
    const searchParams = new URLSearchParams();
    if (params?.format) searchParams.append('format', params.format);
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    
    const response = await api.get(`/api/admin/audit-logs/export?${searchParams.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  exportUserData: async (userId: number, format?: string): Promise<any> => {
    const response = await api.post(`/api/admin/gdpr/export-user-data/${userId}`, {
      format: format || 'json'
    }, {
      responseType: 'blob'
    });
    return response.data;
  },

  deleteUserData: async (userId: number, reason?: string): Promise<any> => {
    const response = await api.post(`/api/admin/gdpr/delete-user-data/${userId}`, {
      reason: reason || 'right_to_be_forgotten'
    });
    return response.data;
  },

  cleanupExpiredData: async (dataType?: string): Promise<any> => {
    const response = await api.post('/api/admin/gdpr/cleanup-expired-data', {
      data_type: dataType
    });
    return response.data;
  },

  getRetentionReport: async (): Promise<any> => {
    const response = await api.get('/api/admin/gdpr/retention-report');
    return response.data;
  },

  runSecurityAssessment: async (): Promise<any> => {
    const response = await api.post('/api/admin/soc2/security-assessment');
    return response.data;
  },

  // API Key Management
  generateApiKey: async (name: string, expiresInDays?: number): Promise<any> => {
    const response = await api.post('/api/keys/generate', {
      name,
      expires_in_days: expiresInDays
    });
    return response.data;
  },

  listApiKeys: async (): Promise<any> => {
    const response = await api.get('/api/keys/list');
    return response.data;
  },

  revokeApiKey: async (keyId: number): Promise<any> => {
    const response = await api.delete(`/api/keys/revoke/${keyId}`);
    return response.data;
  },

  updateApiKey: async (keyId: number, name: string): Promise<any> => {
    const response = await api.patch(`/api/keys/update/${keyId}`, { name });
    return response.data;
  }
};

export default api;
